import { useState, useEffect } from 'react';
import { initializeCommunity, isUserConsented } from '@atlassian/community-library';
import { UserContext } from 'context/UserContext';
import { useContext } from 'react';
import { sendNewAccountEvent } from '../utils/send-newaccount-event';

type UseCommunityInitializationResult = {
  isChecking: boolean;
  userHasConsented: boolean;
  userIsInitialized: boolean;
  initializationError: boolean;
};

export const useCommunityInitialization = (): UseCommunityInitializationResult => {
  const data = useContext(UserContext);
  const isLoggedIn = !!data?.user;

  // Check for SSR state to avoid unnecessary API calls
  const isSSR = typeof window !== 'undefined' && window.isSSR;
  const ssrUserConsented = typeof window !== 'undefined' ? window.__IS_CONSENTED__ : undefined;
  const ssrUserInitialized = typeof window !== 'undefined' ? window.__ACCOUNTS_INITIALIZED__ : undefined;

  // Track consent status and initialization status
  const [userHasConsented, setUserHasConsented] = useState(false);
  const [userIsInitialized, setUserIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const isLocal = process.env['REACT_APP_ENVI'] === 'local';
  const homepage = isLocal ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  useEffect(() => {
    if (window?.AtlOneTrustWrapper?.updatePolicyLinks) {
      window.AtlOneTrustWrapper.updatePolicyLinks();
    }
  }, []);

  // Check if the user has consented
  useEffect(() => {
    const checkConsent = async () => {
      // Skip consent check if not logged in
      if (!isLoggedIn || !data?.user?.account_id) {
        setUserHasConsented(false);
        setIsChecking(false);
        return;
      }

      // For SSR users who are already consented and initialized, use the SSR state
      if (isSSR && ssrUserConsented && ssrUserInitialized) {
        setUserHasConsented(true);
        setUserIsInitialized(true);
        setInitializationError(false);
        setIsChecking(false);
        return;
      }

      try {
        const consentResult = await isUserConsented(
          ['communityTermsOfService', 'privacyPolicy'],
          data.user.account_id,
          homepage,
          homepage,
        );

        setUserHasConsented(consentResult);
        // Reset initialization state when consent changes
        setUserIsInitialized(false);
        setInitializationError(false);
      } catch (error) {
        console.error('[useCommunityInitialization] Error checking consent status:', error);
        setUserHasConsented(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkConsent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.user?.account_id, isLoggedIn, isSSR, ssrUserConsented, ssrUserInitialized]);

  // Handle initialization after we know consent status
  useEffect(
    () => {
      /* Only initialize if:
       1. User is logged in
       2. User has consented
       3. User is not already initialized
       4. Not already initialized via SSR */
      const userPassedConsentCheck =
        isLoggedIn &&
        userHasConsented &&
        data?.user?.account_id &&
        !userIsInitialized &&
        !isChecking &&
        !(isSSR && ssrUserInitialized);
      const initializeUser = async () => {
        if (userPassedConsentCheck) {
          try {
            console.log('[useCommunityInitialization] Initializing community for user:', data.user!.account_id);
            const response = await initializeCommunity(data.user!.email, homepage, homepage);

            if (!response) {
              console.error('[useCommunityInitialization] Community initialization failed - no response');
              setInitializationError(true);
            } else {
              await sendNewAccountEvent(response?.isNewForumsProfile, data.user?.account_id);
              setUserIsInitialized(response?.success);
              setInitializationError(false);
            }
          } catch (error) {
            console.error('[useCommunityInitialization] Error initializing community:', error);
            setInitializationError(true);
          }
        }
      };

      initializeUser();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isChecking,
      isLoggedIn,
      userHasConsented,
      userIsInitialized,
      data?.user?.account_id,
      isSSR,
      ssrUserInitialized,
      data?.user,
    ],
  );

  return {
    isChecking,
    userHasConsented,
    userIsInitialized,
    initializationError,
  };
};
